/**
 * IXD 값 편집기 페이지 UI 상태 스토어
 * 페이지 스코프로 동작하며, Provider 언마운트 시 상태가 초기화됩니다.
 *
 * 셀 편집 상태/undo/raw value 토글/저장·다운로드는
 * `EditSessionStoreProvider` + `useIxdEditSession`에서 공용으로 관리합니다.
 */

import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import type { FsType } from "@/apis/ixd-editor/types";

export interface IxdEditorState {
  selectedFsType: FsType;
  setSelectedFsType: (fsType: FsType) => void;
  activeChunkId: string | null;
  setActiveChunkId: (id: string | null) => void;
  reset: () => void;
}

export const createIxdEditorStore = () =>
  createStore<IxdEditorState>()((set) => ({
    selectedFsType: "consolidated" as FsType,
    setSelectedFsType: (selectedFsType) =>
      set({ selectedFsType, activeChunkId: null }),
    activeChunkId: null,
    setActiveChunkId: (activeChunkId) => set({ activeChunkId }),

    reset: () =>
      set({
        selectedFsType: "consolidated",
        activeChunkId: null,
      }),
  }));

export type IxdEditorStore = ReturnType<typeof createIxdEditorStore>;

export const IxdEditorStoreContext = createContext<IxdEditorStore | null>(null);

export const useIxdEditorStore = <T>(
  selector: (state: IxdEditorState) => T,
): T => {
  const store = useContext(IxdEditorStoreContext);
  if (!store) {
    throw new Error(
      "useIxdEditorStore must be used within IxdEditorStoreProvider",
    );
  }
  return useStore(store, selector);
};
